import uniq from 'lodash/uniq';

import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptRelationship,
  isModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addUnconnectedModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addUnconnectedModelConceptRelationship';
import {
  createModelConceptRelationship,
  getModelConceptRelationshipKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import {
  createModelConceptRelationshipLabel,
  getModelConceptRelationshipLabelKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { restoreDeletedRelationship } from 'src/containers/ProjectDiagram/utils/restoreDeletedData';

export const addRelationshipData = ({
  fromIri,
  value,
  toIri,
  model,
}: {
  fromIri: string;
  value: string;
  toIri: string;
  model: Model;
}) => {
  let addedRelationship: ModelConceptRelationship | undefined;
  let addedRelationshipLabel: ModelConceptRelationshipLabel | undefined;

  const relationshipKey = getModelConceptRelationshipKey({
    fromIri,
    value,
    toIri,
  });
  const existingRelationship = model.linkDataArray.find(
    (linkData) =>
      isModelConceptRelationship(linkData) && linkData.key === relationshipKey
  );
  if (!existingRelationship) {
    addedRelationship = createModelConceptRelationship({
      fromIri,
      value,
      toIri,
    });
    model.addLinkData(addedRelationship);
  }

  const relationshipLabelKey = getModelConceptRelationshipLabelKey({
    fromIri,
    value,
    toIri,
  });
  const existingRelationshipLabel = model.nodeDataArray.find(
    (linkData) =>
      isModelConceptRelationshipLabel(linkData) &&
      linkData.key === relationshipLabelKey
  );
  if (!existingRelationshipLabel) {
    addedRelationshipLabel = createModelConceptRelationshipLabel({
      fromIri,
      value,
      toIri,
    });
    model.addNodeData(addedRelationshipLabel);
  }

  if (addedRelationship && addedRelationshipLabel) {
    restoreDeletedRelationship(
      addedRelationship,
      addedRelationshipLabel
    )(model);
  }

  return { addedRelationship, addedRelationshipLabel };
};

export const addModelConceptRelationship =
  ({
    classIri,
    direction,
    iri,
    addUnconnectedRelationship = true,
  }: {
    classIri: string;
    direction: RelationshipDirection;
    iri: string;
    addUnconnectedRelationship?: boolean;
  }) =>
  (model: Model, diagram?: Diagram) => {
    const otherRelationships = model.linkDataArray.filter(
      (data) => isModelConceptRelationship(data) && data.value === iri
    );

    const addedRelationships: ModelConceptRelationship[] = [];
    const addedRelationshipLabels: ModelConceptRelationshipLabel[] = [];

    if (direction === RelationshipDirection.OUTGOING) {
      const rangeIris = uniq(
        otherRelationships
          .filter((relationship) => typeof relationship.to === 'string')
          .map((relationship) =>
            getModelConceptClassValue(relationship.to as string)
          )
      );
      if (rangeIris.length) {
        rangeIris.forEach((rangeIri) => {
          const { addedRelationship, addedRelationshipLabel } =
            addRelationshipData({
              fromIri: classIri,
              value: iri,
              toIri: rangeIri,
              model,
            });
          if (addedRelationship) {
            addedRelationships.push(addedRelationship);
          }
          if (addedRelationshipLabel) {
            addedRelationshipLabels.push(addedRelationshipLabel);
          }
        });
      } else if (addUnconnectedRelationship) {
        addUnconnectedModelConceptRelationship({
          value: iri,
          fromIri: classIri,
        })(model, diagram);
      }
    } else {
      const domainIris = uniq(
        otherRelationships
          .filter((relationship) => typeof relationship.from === 'string')
          .map((relationship) =>
            getModelConceptClassValue(relationship.from as string)
          )
      );
      if (domainIris.length) {
        domainIris.forEach((domainIri) => {
          const { addedRelationship, addedRelationshipLabel } =
            addRelationshipData({
              fromIri: domainIri,
              value: iri,
              toIri: classIri,
              model,
            });
          if (addedRelationship) {
            addedRelationships.push(addedRelationship);
          }
          if (addedRelationshipLabel) {
            addedRelationshipLabels.push(addedRelationshipLabel);
          }
        });
      } else if (addUnconnectedRelationship) {
        addUnconnectedModelConceptRelationship({
          value: iri,
          toIri: classIri,
        })(model, diagram);
      }
    }

    return { addedRelationships, addedRelationshipLabels };
  };
